import _ from 'lodash';
import { createSelector } from 'reselect';

import FIELD_TYPES from '../../common/formFields/fieldTypes';
import ExperienceSelectors from '../experience/experience-selectors';
import FormSelectors from '../form/form-selectors';
import PageCollection from './page-collection';
import FormCollection from '../form/form-collection';
import PageUtils from './page-utils';

const _scopedState = (state) => state.entities.page;

const pagesById = (state) => _scopedState(state).byId;

const pageIdList = createSelector(pagesById, (_pagesById) => _.map(_.keys(_pagesById), (k) => parseInt(k, 10)));

const pages = createSelector(pagesById, (_pagesById) => PageCollection.create(_.values(_pagesById)));

const isFetching = (state) => _scopedState(state).isFetching;
const pageById = (state, pageId) => pagesById(state)[pageId];

const apiErrorMessage = (state) => _scopedState(state).apiErrorMessage;
const isSaving = (state) => _scopedState(state).isSaving;
const isSaveSuccess = (state) => _scopedState(state).isSaveSuccess;
const isSaveError = (state) => _scopedState(state).isSaveError;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Page phases.

const byPhase = (state) => _scopedState(state).byPhase;

const phases = createSelector([byPhase], (byPhase) => _.keys(byPhase));

const pagesInPhase = createSelector([byPhase, (state, phase) => phase], (byPhase, phase) => byPhase[phase]);

/**
 * Orders the pages by phases. Gives an array of page ids as they will appear in the SD left sidebar or in the build section
 * Can be used to find out if a page is before or after another page.
 */
const getOrderedLayoutIds = createSelector([byPhase], (byPhase) =>
  _.reduce(byPhase, (acc, layoutIds) => acc.concat(layoutIds), [])
);

const displayPhaseName = {
  prelive:  'Countdown',
  entry:    'Entry',
  judge:    'Judging',
  vote:     'Voting',
  announce: 'Winner Announcement',
  postlive: 'Curtain',
};

const _emptyPhases = (state) => _scopedState(state).emptyPhases;

const emptyPhases = createSelector([_emptyPhases], (emptyPhases) =>
  emptyPhases.map((phase) => displayPhaseName[phase]).join(', ')
);

const getCountdownPage = createSelector([pages], (pages) => pages.find((page) => page.isCountdownPage));

const getCurtainPage = createSelector([pages], (pages) => pages.find((page) => page.isCurtainPage));

/**
 * Returns a correct entryPage object for both Macro (by getting directly the form) and NextGen (by walking through all pages and inspecting sections, to find a 'form' section) experiences.
 */
const getEntryPage = createSelector(
  pages,
  ExperienceSelectors.experience,
  FormSelectors.forms,
  (pages, experience, forms) =>
    experience.isMacroExperience
      ? FormCollection.findById(experience.form, forms)
      : pages.find((page) => _.filter(page.layoutConfig.pageLayout.sections, { type: 'form' }).length > 0)
);

const hasCountdownPageOrCurtainPage = createSelector(
  [getCountdownPage, getCurtainPage],
  (countdownPage, courtainPage) => countdownPage || courtainPage
);

/**
 * Returns a list of remote id fields from the entry page (if there is one)
 */
const getRemoteFieldIds = createSelector(getEntryPage, (entryPage) => {
  const modules = _.get(entryPage, 'layoutConfig.modules', []);
  const remoteFieldIds = _.map(modules, (m) => _.get(m, 'data.remoteField', ''));
  return _.filter(_.uniq(remoteFieldIds), (m) => !_.isEmpty(m));
});

/**
 * Returns a list of remote id fields from the entry page (if there is one) with some changes regarding the integrations logic
 */
const getRemoteFieldIdsForIntegrations = createSelector(getEntryPage, (entryPage) => {
  const modules = _.get(entryPage, 'layoutConfig.modules', []);
  // partition of modules to address modules and other modules, cause just it's remote name is not working in integrations
  const [addressModules, otherModules] = _.partition(modules, (m) => m.type === FIELD_TYPES.ADDRESS);

  const remoteFieldIds = _.map(otherModules, (m) => _.get(m, 'data.remoteField', ''));

  const extendedAddressRemoteIds = [];
  if (addressModules.length) {
    _.each(addressModules, (module) => {
      // static fields used in address modules
      _.each(
        [
          '_Address1',
          '_Address2',
          '_City',
          '_State',
          '_Zip',
          ...(_.get(module, 'data.includeCountry', false) ? ['_Country'] : []),
        ],
        (addressField) => {
          extendedAddressRemoteIds.push(`${_.get(module, 'data.remoteField', '')}${addressField}`);
        }
      );
    });
  }

  return _.filter(_.uniq([...remoteFieldIds, ...extendedAddressRemoteIds].sort()), (m) => !_.isEmpty(m));
});

/**
 * Returns a list of hidden fields from the entry page form section (if there is one)
 */
const getHiddenFields = createSelector(getEntryPage, (entryPage) => {
  const formSection = _.find(_.get(entryPage, 'layoutConfig.pageLayout.sections', []), { type: 'form' }) || {};
  return _.get(formSection, 'data.hiddenFields', []);
});

//We need to determine if the context of an experience UGC has changed, while it is not a perfect assumption we can suspect that it may have happened. If we realize that the UGC status might change, we later trigger an experience data refresh, via an API call, to make sure that we have the correct and up to date information regarding the experience UGC status. This is specially relevant for the entry page, as it may contain an upload field.
const pageHasUGCField = createSelector(getEntryPage, (entryPage) => {
  const modules = _.get(entryPage, 'layoutConfig.modules', []);

  return !!_.find(modules, (module) => module.type === FIELD_TYPES.UPLOAD);
});

/**
 * Get Opt-In fields
 */
const getOptInFields = createSelector(getEntryPage, (entryPage) => {
  const OPT_IN_REMOTE_FIELD_MAPPING = {
    brand_opt_in:       'brandOptIn',
    group_opt_in:       'groupOptIn',
    third_party_opt_in: 'thirdPartyOptIn',
  };

  if (!entryPage) {
    // on account level just return all of them
    return [
      OPT_IN_REMOTE_FIELD_MAPPING.brand_opt_in,
      OPT_IN_REMOTE_FIELD_MAPPING.group_opt_in,
      OPT_IN_REMOTE_FIELD_MAPPING.third_party_opt_in,
    ];
  }

  const modules = _.get(entryPage, 'layoutConfig.modules', []);

  return _.uniq(
    modules.reduce((result, m) => {
      // Filter by type first
      if (![FIELD_TYPES.MULTI_OPTIN, FIELD_TYPES.MARKETING].includes(m.type)) {
        return result;
      }
      const optIn = _.get(m, 'data', {});
      let mappedValue;
      // Transform the optIn
      if (OPT_IN_REMOTE_FIELD_MAPPING[optIn.remoteField]) {
        mappedValue = OPT_IN_REMOTE_FIELD_MAPPING[optIn.remoteField];
      } else {
        const multiOptInType = optIn.profile && optIn.profile.split('_list')[0];
        if (OPT_IN_REMOTE_FIELD_MAPPING[multiOptInType]) {
          mappedValue = OPT_IN_REMOTE_FIELD_MAPPING[multiOptInType];
        }
      }
      // Add to result if not empty and not already present
      if (!_.isEmpty(mappedValue)) {
        result.push(mappedValue);
      }
      return result;
    }, [])
  );
});

/**
 * Selector for getting page dwell slide information
 */
const getPagesDwellRowsInfo = createSelector([pages, getOrderedLayoutIds], (pages, orderedLayoutIds) =>
  PageUtils.getDwellPagesInfo(pages, orderedLayoutIds, true)
);

/**
 * Selector for getting page dwell information
 */
const getDwellPagesInfo = createSelector([pages, getOrderedLayoutIds], (pages, orderedLayoutIds) =>
  PageUtils.getDwellPagesInfo(pages, orderedLayoutIds)
);

export default {
  pages,
  pagesById,
  pageById,
  pageIdList,
  isFetching,
  apiErrorMessage,
  isSaving,
  isSaveSuccess,
  isSaveError,

  phases,
  pagesInPhase,
  byPhase,
  emptyPhases,
  getCurtainPage,
  getCountdownPage,
  getEntryPage,
  getRemoteFieldIds,
  getRemoteFieldIdsForIntegrations,
  getHiddenFields,
  getOptInFields,
  getPagesDwellRowsInfo,
  getDwellPagesInfo,
  getOrderedLayoutIds,
  hasCountdownPageOrCurtainPage,

  pageHasUGCField,
};
